import { BASE_URL } from 'common/config.js'
import { tokenService } from 'common/token-service.js'
import { reachCheckpoint } from 'hooks/usePerfStep.js'
import { setMeasurement } from 'src/common/sentry-client'
import emitter from '@/services/event-emitter.js'
import { SSE_CONFIG } from '@/services/fast-data/sse-config.js'
import { connectToWs } from '@/services/fast-data/websocket-service.js'

let eventSource = null
let reconnectAttempts = 0
let sseFailureCount = 0
let failureResetTimer = null
let pendingConnectTimer = null
let reconnectTimer = null
let isListenersAttached = false
let authTokenOverride = null
let eventSourceToken = null

let sseInstanceId = 0
const sseActiveInstances = new Set()

let firstWsConnected = false

export function connectToSSE(options = {}) {
  updateConnectionOptions(options)

  if (eventSource) {
    if (eventSourceToken === getAuthToken()) {
      return
    }

    clearTimers()
    closeEventSource()
  }

  setMeasurement('app.sse_connect_attempt_count', 1, 'none')

  sseInstanceId++
  const currentId = sseInstanceId

  sseActiveInstances.add(currentId)

  attachEventListenersOnce()

  const url = process.env.SSE_DOMAIN || `https://${location.hostname}${BASE_URL}/api/g/sse/`
  eventSourceToken = getAuthToken()
  const queryString = buildQueryParams(eventSourceToken)

  eventSource = new EventSource(`${url}?${queryString}`, { withCredentials: false })

  pendingConnectTimer = setTimeout(() => {
    if (eventSource && eventSource.readyState === EventSource.CONNECTING) {
      console.warn('⚠️ SSE connection stuck in CONNECTING state')
      handleSSEFailure()
    }
  }, SSE_CONFIG.CONNECT_TIMEOUT)

  eventSource.onopen = () => {
    if (!firstWsConnected) {
      reachCheckpoint('app.sse_connect_time')
      firstWsConnected = true
    }

    setMeasurement('app.sse_connect_success_count', 1, 'none')

    reconnectAttempts = 0
    sseFailureCount = 0
    clearTimers()
  }

  eventSource.onmessage = (event) => {
    try {
      const data = JSON.parse(event.data)
      emitter.emit(data.message_type, data.content)
    } catch (error) {
      console.error('Error parsing SSE message:', error)
    }
  }

  eventSource.onerror = () => {
    setMeasurement('app.sse_connect_failure_count', 1, 'none')
    handleSSEFailure()
  }
}

function handleSSEFailure() {
  clearTimers()
  closeEventSource()

  sseFailureCount++

  if (sseFailureCount >= SSE_CONFIG.MAX_FAILURES_BEFORE_FALLBACK) {
    console.error('❌ SSE unstable, switching to WebSocket...')
    connectToWs(authTokenOverride ? { token: authTokenOverride } : undefined)
    return
  }

  if (!failureResetTimer) {
    failureResetTimer = setTimeout(() => {
      sseFailureCount = 0
    }, SSE_CONFIG.FAILURE_RESET_TIME)
  }

  reconnectAttempts++
  const delay =
    reconnectAttempts < SSE_CONFIG.MAX_RECONNECT_ATTEMPTS ? (reconnectAttempts + 1) * 2000 : SSE_CONFIG.FIXED_DELAY

  scheduleReconnect(delay)
}

function clearTimers() {
  if (failureResetTimer) {
    clearTimeout(failureResetTimer)
    failureResetTimer = null
  }

  if (pendingConnectTimer) {
    clearTimeout(pendingConnectTimer)
    pendingConnectTimer = null
  }

  if (reconnectTimer) {
    clearTimeout(reconnectTimer)
    reconnectTimer = null
  }
}

function buildQueryParams(token) {
  const params = new URLSearchParams()
  if (token) {
    params.append('token', token)
  }
  params.append('nocache', Date.now())
  return params.toString()
}

function connectWithCheckingState() {
  if (!eventSource || eventSource.readyState === EventSource.CLOSED) {
    connectToSSE()
  }
}

function attachEventListenersOnce() {
  if (isListenersAttached) {
    return
  }
  isListenersAttached = true

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      if (!eventSource) {
        // eslint-disable-next-line no-console
        console.log('[CAS] 🔄 Restoring SSE connection after returning to the page...')
        connectToSSE()
      } else {
        connectWithCheckingState()
      }
    }
  })

  window.addEventListener('online', () => {
    if (!eventSource) {
      // eslint-disable-next-line no-console
      console.log('[CAS] 🌍 Network restored, reconnecting SSE...')
      connectToSSE()
    } else {
      connectWithCheckingState()
    }
  })
}

function updateConnectionOptions(options) {
  const normalizedOptions = typeof options === 'string' ? { token: options } : options || {}

  if (Object.prototype.hasOwnProperty.call(normalizedOptions, 'token')) {
    authTokenOverride = normalizedOptions.token || null
  }
}

function getAuthToken() {
  return authTokenOverride || tokenService.getToken()
}

function closeEventSource() {
  if (!eventSource) {
    return
  }

  sseActiveInstances.delete(sseInstanceId)
  eventSource.close()
  eventSource = null
  eventSourceToken = null
}

function scheduleReconnect(delay) {
  if (reconnectTimer) {
    return
  }

  reconnectTimer = setTimeout(() => {
    reconnectTimer = null
    connectToSSE()
  }, delay)
}
